import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES, homeFor } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ChevronDown, LogIn, UserPlus, Menu, Search, Info,
  Stethoscope, Phone, LayoutDashboard, Building2,
  CreditCard, ClipboardList, Share2, LogOut, Check, ExternalLink,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function PublicHeader() {
  const { lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const isMember = user?.role === ROLES.MEMBER;

  const navLinks = [
    { to: "/search",          label: lang === "ar" ? "العروض المتاحة"       : "Available Deals",  icon: Search },
    { to: "/about",           label: lang === "ar" ? "من نحن"               : "About Us",         icon: Info },
    { to: "/medical-tourism", label: lang === "ar" ? "السياحة العلاجية"     : "Medical Tourism",  icon: Stethoscope },
    { to: "/faq",             label: lang === "ar" ? "الأسئلة الشائعة"       : "FAQ",              icon: HelpCircle },
    { to: "/contact",         label: lang === "ar" ? "تواصل معنا"           : "Contact",          icon: Phone },
  ];

  const memberNavItems = [
    { to: "/member/dashboard", labelEn: "Dashboard",  labelAr: "لوحة التحكم",   Icon: LayoutDashboard },
    { to: "/member/card",      labelEn: "My Card",    labelAr: "بطاقتي",        Icon: CreditCard },
    { to: "/member/home",      labelEn: "Find Care",  labelAr: "ابحث عن رعاية", Icon: Search },
    { to: "/member/visits",    labelEn: "My Visits",  labelAr: "زياراتي",       Icon: ClipboardList },
  ];

  const handleCopyReferral = () => {
    if (!user) return;
    const link = `${window.location.origin}/signup?ref=${user.member_code || ""}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      toast.success(lang === "ar" ? "تم نسخ رابط الدعوة" : "Referral link copied!");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // First name or fallback
  const displayName = user?.full_name
    ? user.full_name.split(" ")[0]
    : (lang === "ar" ? "حسابي" : "My Account");

  const avatarInitial = user?.full_name
    ? user.full_name.charAt(0).toUpperCase()
    : "M";

  return (
    <header className="fixed top-0 start-0 end-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

        {/* Logo */}
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/home")}>
          <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10" data-testid="hemaia-logo" />
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {label}
            </Link>
          ))}

          {/* For Providers mini-dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {lang === "ar" ? "للمستشفيات والعيادات" : "For Providers"}
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={lang === "ar" ? "start" : "end"}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="min-w-[190px] p-1.5"
            >
              <DropdownMenuItem onClick={() => navigate("/for-providers")} className="gap-3 px-3 py-2.5 rounded-lg cursor-pointer">
                <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium">{lang === "ar" ? "معرفة المزيد" : "Learn more"}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem onClick={() => navigate("/provider-signup")} className="gap-3 px-3 py-2.5 rounded-lg cursor-pointer">
                <Building2 className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-primary">{lang === "ar" ? "سجّل عيادتك" : "Register your clinic"}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-2">

          {/* Language switcher — single text link */}
          <button
            onClick={toggle}
            className="text-sm font-medium text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
            data-testid="lang-toggle"
          >
            {lang === "ar"
              ? <span style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>English</span>
              : <span>عربي</span>
            }
          </button>

          {/* Auth — desktop only */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              isMember ? (
                /* ── Member avatar + dropdown ─────────────────────────── */
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-accent transition-colors" data-testid="member-menu-btn">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-primary">{avatarInitial}</span>
                      </div>
                      <span className="text-sm font-medium">{displayName}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align={lang === "ar" ? "start" : "end"}
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    className="min-w-[200px] p-1.5"
                  >
                    {memberNavItems.map(({ to, labelEn, labelAr, Icon }) => (
                      <DropdownMenuItem key={to} onClick={() => navigate(to)} className="gap-3 px-3 py-2.5 rounded-lg cursor-pointer">
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium">{lang === "ar" ? labelAr : labelEn}</span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem onClick={handleCopyReferral} className="gap-3 px-3 py-2.5 rounded-lg cursor-pointer">
                      {copied
                        ? <Check className="h-4 w-4 text-green-500 shrink-0" />
                        : <Share2 className="h-4 w-4 text-muted-foreground shrink-0" />}
                      <span className="text-sm font-medium">{lang === "ar" ? "دعوة صديق" : "Invite Friends"}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem onClick={handleLogout} className="gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-destructive focus:text-destructive">
                      <LogOut className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">{lang === "ar" ? "تسجيل الخروج" : "Sign Out"}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                /* ── Admin / Provider dashboard button ────────────────── */
                <Button onClick={() => navigate(homeFor(user.role))} variant="default" size="sm" className="rounded-xl" data-testid="go-dashboard-btn">
                  {lang === "ar" ? "لوحة التحكم" : "Dashboard"}
                </Button>
              )
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="default" size="sm" className="rounded-xl gap-1.5" data-testid="auth-menu-btn">
                    {lang === "ar" ? "انضم الآن" : "Join Now"}
                    <ChevronDown className="h-3.5 w-3.5 opacity-80" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[170px]">
                  <DropdownMenuItem onClick={() => navigate("/signup")} className="gap-2 py-2.5" data-testid="signup-btn">
                    <UserPlus className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium text-sm">{lang === "ar" ? "إنشاء حساب" : "Create Account"}</div>
                      <div className="text-xs text-muted-foreground">{lang === "ar" ? "عضوية جديدة" : "New membership"}</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/login")} className="gap-2 py-2.5" data-testid="login-btn">
                    <LogIn className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium text-sm">{lang === "ar" ? "تسجيل دخول" : "Sign In"}</div>
                      <div className="text-xs text-muted-foreground">{lang === "ar" ? "لديك حساب بالفعل" : "Already have an account"}</div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Hamburger — mobile only */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-accent transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0 flex flex-col">

              {/* Sheet header */}
              <div className="flex items-center gap-2 px-5 py-4 border-b">
                <img src="/hemaia_logo.svg" alt="Hemaia" className="h-8" />
              </div>

              {/* Nav links */}
              <nav className="flex flex-col flex-1 px-3 py-4 gap-1">
                {navLinks.map(({ to, label, icon: Icon }) => (
                  <SheetClose asChild key={to}>
                    <Link
                      to={to}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {label}
                    </Link>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    to="/for-providers"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 shrink-0" />
                    {lang === "ar" ? "للمستشفيات والعيادات" : "For Providers"}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/provider-signup"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-primary hover:bg-primary/5 transition-colors"
                  >
                    <Building2 className="h-4 w-4 shrink-0" />
                    {lang === "ar" ? "سجّل عيادتك" : "Register your clinic"}
                  </Link>
                </SheetClose>
              </nav>

              {/* Mobile auth */}
              <div className="px-4 pb-6 pt-2 border-t flex flex-col gap-2">
                {user ? (
                  isMember ? (
                    <>
                      {memberNavItems.map(({ to, labelEn, labelAr, Icon }) => (
                        <SheetClose asChild key={to}>
                          <Button onClick={() => navigate(to)} variant="ghost" className="w-full rounded-xl gap-2 justify-start">
                            <Icon className="h-4 w-4" />
                            {lang === "ar" ? labelAr : labelEn}
                          </Button>
                        </SheetClose>
                      ))}
                      <SheetClose asChild>
                        <Button onClick={handleLogout} variant="ghost" className="w-full rounded-xl gap-2 justify-start text-destructive hover:text-destructive">
                          <LogOut className="h-4 w-4" />
                          {lang === "ar" ? "تسجيل الخروج" : "Sign Out"}
                        </Button>
                      </SheetClose>
                    </>
                  ) : (
                    <SheetClose asChild>
                      <Button onClick={() => navigate("/")} variant="default" className="w-full rounded-xl gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        {lang === "ar" ? "لوحة التحكم" : "Dashboard"}
                      </Button>
                    </SheetClose>
                  )
                ) : (
                  <>
                    <SheetClose asChild>
                      <Button onClick={() => navigate("/signup")} variant="default" className="w-full rounded-xl gap-2">
                        <UserPlus className="h-4 w-4" />
                        {lang === "ar" ? "إنشاء حساب" : "Create Account"}
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button onClick={() => navigate("/login")} variant="outline" className="w-full rounded-xl gap-2">
                        <LogIn className="h-4 w-4" />
                        {lang === "ar" ? "تسجيل دخول" : "Sign In"}
                      </Button>
                    </SheetClose>
                  </>
                )}
              </div>

            </SheetContent>
          </Sheet>

        </div>
      </div>
    </header>
  );
}
